import { useCustomerDetails } from '@egw/api'
import { useAuth } from '@egw/auth'
import { Loader } from '@egw/components'
import { MODAL_CONTAINER_ID, WEALTH_ROUTES, type FeatureFlags, type RouteValue } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useFlags } from '@egw/feature-flags'
import {
  AUTH_ROUTES,
  EnterPhonePage,
  ExpiredApplicationPage,
  FeatureFlagGuard,
  IdleTimer,
  VerifyEmailPage,
  VerifyPhonePage,
  WelcomeBackPage,
} from '@egw/features-auth'
import { Enrollment } from '@egw/features-enrollment'
import { ALL_ENROLLMENT_ROUTES } from '@egw/features-enrollment/src/constants'
import { RefreshPage } from '@egw/features-system'
import { useTranslation } from '@egw/i18n'
import { Layout, MaintenancePage } from '@egw/layouts'
import { notifications, NOTIFICATIONS } from '@egw/notifications'
import { RouterFramework, RouterProvider } from '@egw/router'
import { useEffect, useMemo, useState, type FC, type PropsWithChildren } from 'react'
import { BrowserRouter, matchPath, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router'
import { HomePage } from './home'
// Auth
import { SignInPage } from './sign-in'
// Overview
import { OverviewPage } from './overview'
// Checking
import { AccountActivatePage } from './activate'
import { CheckingAccountPage } from './checking'
import { CheckingAccountActivityPage } from './checking/activity'
import { CheckingActivityDetailsPage } from './checking/activity/details'
import { CheckingAccountDebitCardPage } from './checking/debit-card'
import { CheckingAccountDebitCardActivatePage } from './checking/debit-card-activate'
import { CheckingAccountDetailsPage } from './checking/details'
// Savings
import { SavingsAccountPage } from './savings'
import { SavingsAccountActivityPage } from './savings/activity'
import { SavingsActivityDetailsPage } from './savings/activity/details'
import { SavingsAccountDetailsPage } from './savings/details'
// Settings
import { SettingsPage } from './settings'
import { SettingsExternalAccountsPage } from './settings/bank-transfer-accounts'
import { SettingsExternalAccountDetailsPage } from './settings/bank-transfer-accounts/detail'
import { SettingsPasskeyPage } from './settings/passkey'
import { SettingsPasskeyDetailsPage } from './settings/passkey/details'
import { SettingsPersonalInfoPage } from './settings/personal-info'
// Documents
import { DocumentsPage } from './documents'
import { LegalPartnerPage } from './documents/legal-partner'
import { StatementsPage } from './documents/statements'
import { StatementsByDatePage } from './documents/statements-date'
import { TaxesByDatePage } from './documents/taxes-date'
import { TradeConfirmationsPage } from './documents/trade-confirmations'
// Transfer
import { AchTransferPage } from './transfer'
import { RolloverIRAInfoPage } from './transfer/rollover-ira'
import { WireTransferPage } from './transfer/wire'
// Help center
import { HelpCenterPage } from './help-center'
import { SettingsBeneficiariesPage } from './settings/beneficiaries'
import { ReviewWirePage } from './transfer/wire/review'
import { SuccessWirePage } from './transfer/wire/success'
// Personal Foundation
import { PersonalFoundationPage } from './personal-foundation'
// Passkey Recovery
import { DebitCardActivateSuccessPage } from './checking/debit-card-activate-success'
import { DebitCardSetPinPage } from './checking/debit-card-set-pin'
// Investmets
import { InvestmentsPage } from './investments'
import { InvestmentsAccountPage } from './investments/account'
import { InvestmentsDetailsPage } from './investments/account-detail'
import { InvestmentsAccountActivityPage } from './investments/activity'
import { InvestmentsActivityDetailsPage } from './investments/activity/details'
import { InvestmentsActivityTradesPage } from './investments/activity/details/trades'
import { InvestmentsActivityTradesDetailsPage } from './investments/activity/details/trades/details'
import { InvestmentsOverviewHoldingsPage } from './investments/holdings/overview-holdings'
import { InvestmentsOverviewHoldingsDetailsPage } from './investments/holdings/overview-holdings-details'
import { InvestmentOverviewPage } from './investments/oververview'
// Cash Accounts
import { CashAccountsOverviewPage } from './cash-accounts-overview'
// Evergreen Accounts
import { EvergreenAccountsPage } from './evergreen-accounts'
import { LinkedAccountsPage } from './linked-accounts'
// Maintenance
import { NotFound404Page } from './not-found-404'
// Passkey Recovery
import { ApplicationUnderReviewPage } from './application-under-review'
import { EvergreenIntelligencePage } from './evergreen-intelligence'
import { InvestmentsAccountHoldingsDetailsPage } from './investments/holdings/account-holdings-details/account-holdings-details'
import { InvestmentsAccountHoldingsPage } from './investments/holdings/account-holdings/account-holdings'
import { InvestmentsAllocationHoldingsDetailsPage } from './investments/holdings/allocation-holdings-details/allocation-holdings-details'
import { InvestmentsAllocationHoldingsListPage } from './investments/holdings/allocation-holdings-list/allocation-holdings-list'
import { InvestmentsCompanionAccountHoldingDetailsPage } from './investments/holdings/companion-account-holdings-details/companion-account-holdings-details'
import { InvestmentsCompanionAccountHoldingsPage } from './investments/holdings/companion-account-holdings/companion-account-holdings'
import { PasskeyRecoveryPage } from './recovery'
import { PasskeyRecoveryMagicLinkPage } from './recovery/magic-link'
import { PasskeyRecoveryMethodPage } from './recovery/method'
import { PasskeyRecoveryPasskeyPage } from './recovery/passkey'
import { PasskeyRecoveryProblemPage } from './recovery/problem'
import { PasskeyRecoveryReviewPage } from './recovery/review'
import { PasskeyRecoverySendEmailPage } from './recovery/send-email'
import { PasskeyRecoverySendPhonePage } from './recovery/send-phone'
import { PasskeyRecoverySubmitPage } from './recovery/submit'
import { PasskeyRecoveryVerificationPage } from './recovery/verification'
import { SignInChallengePage } from './sign-in/challenge'
import { SignInChallengeFailurePage } from './sign-in/challenge/failure'
import { AccountTransferPage } from './transfer/account-transfer'
import { AccountTransferReviewPage } from './transfer/account-transfer/review'
import { BankTransferPage } from './transfer/bank-transfer'

/**
 * Handles location changes and redirects to the sign-in page if the user is not authenticated.
 * Also manages sign-out navigation to ensure clean URLs without redirect parameters.
 * Must be inside the BrowserRouter component of react-router.
 */
const LocationManager: FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()

  // Track when we're in the middle of a sign-out process to prevent timing conflicts.
  // This prevents redirect parameters from being added during sign-out navigation.
  // Without this flag, the auth state updates immediately but location.pathname hasn't
  // updated yet, causing authentication redirect logic to run with stale pathname.
  const [isSigningOut, setIsSigningOut] = useState(false)

  useEffect(() => {
    // Sends event with location information
    // Event is then picked up by Analytics Service and Page Method is invoked
    // Hook runs ever time the location changes
    sendEvent('pageView')
  }, [location.pathname])

  useEffect(() => {
    const userSourceData: Record<string, unknown> = {}

    // Gathers the information on how a user lands on Application
    userSourceData.hostname = window.location.hostname || null
    userSourceData.pathname = window.location.pathname || null
    userSourceData.referrer = window.document.referrer || null
    userSourceData.search = window.location.search || null
    userSourceData.title = window.document.title || null

    // If any query parameters are appended in URL those will be passed into the event as well.
    const searchParams = Object.fromEntries(new URLSearchParams(window.location.search))

    for (const [key, value] of Object.entries(searchParams)) {
      userSourceData[key] = value
    }

    // Sends event with the userSourceData
    // Not necessary to send this to Rudderstack anymore as Rudderstack capture sthis automatically
  }, [])

  // Appends '/enrollment/ to enrollment paths since used via <Outlet /> in the Router they don't currently have it
  // Hence pathnames do not match the actual route paths which affects signing out the user
  const ENROLLMENT_ROUTES = useMemo(() => {
    return Object.entries(ALL_ENROLLMENT_ROUTES)
      .flatMap(([groupName, group]) => {
        const routes = group.ROUTES
        return Object.entries(routes).map(([key, value]) => {
          // Prefix key with group name to prevent duplicates
          const namespacedKey = `${groupName}_${key}`
          return [namespacedKey, value] as [string, RouteValue]
        })
      })
      .reduce<Record<string, RouteValue>>((acc, [key, value]) => {
        acc[key] = {
          ...value,
          path: `/enrollment/${value.path}`,
        }
        return acc
      }, {})
  }, [])

  // Listen for sign-out notifications and navigate to sign-in page without redirect params
  useEffect(() => {
    const handleSignOut = (): void => {
      // Check if the current route is protected before navigating
      const currentRoute = Object.values({ ...WEALTH_ROUTES, ...ENROLLMENT_ROUTES, ...AUTH_ROUTES }).find((r) => {
        return matchPath(r.path, location.pathname)
      })

      // Only navigate to sign-in if the current route is protected
      // This allows users who sign out from public routes to stay on those routes
      if (currentRoute?.isProtected) {
        // Set flag to prevent authentication redirect logic from adding redirect params
        // during the brief moment between auth state change and location update
        setIsSigningOut(true)

        // Navigate directly to sign-in page with replace: true to avoid back button issues
        // No redirect parameter is added - user goes to clean /sign-in URL
        void navigate(WEALTH_ROUTES.SIGN_IN.path, { replace: true })
      }
      // If on a public route, no navigation is needed - user can stay on current page
    }

    // Subscribe to the auth sign-out notification
    notifications.on(NOTIFICATIONS.AUTH.SIGN_OUT, handleSignOut)

    // Clean up listener on component unmount to prevent memory leaks
    return () => {
      notifications.remove(NOTIFICATIONS.AUTH.SIGN_OUT, handleSignOut)
    }
  }, [navigate, location.pathname, ENROLLMENT_ROUTES])

  // Reset the signing out flag once navigation to sign-in page completes
  // This ensures the flag doesn't stay true indefinitely and normal authentication
  // redirect logic can resume for subsequent navigation events
  useEffect(() => {
    if (isSigningOut && location.pathname.startsWith(WEALTH_ROUTES.SIGN_IN.path)) {
      // Navigation completed - reset the flag so authentication logic works normally again
      setIsSigningOut(false)
    }
  }, [isSigningOut, location.pathname])

  const route = useMemo(() => {
    return Object.values({ ...WEALTH_ROUTES, ...ENROLLMENT_ROUTES, ...AUTH_ROUTES }).find((r) => {
      return matchPath(r.path, location.pathname)
    })
  }, [location, ENROLLMENT_ROUTES])

  if ((!route || route.isProtected) && !isAuthenticated) {
    // SIGN-OUT TIMING CONFLICT PREVENTION:
    // Don't redirect if currently in sign-out process. This prevents redirect params
    // from being added during the timing gap between auth state change and location update.
    // Without this check:
    // This logic would redirect to '/sign-in?redirect=/overview' overriding clean navigation
    if (isSigningOut) {
      return children
    }

    // Don't redirect if already on sign-in page or sub-routes (prevents navigation conflicts)
    // Uses startsWith to handle sign-in sub-routes like /sign-in/challenge, /sign-in/recovery, etc.
    if (location.pathname.startsWith(WEALTH_ROUTES.SIGN_IN.path)) {
      return children
    }

    // Standard authentication redirect logic - add redirect param for non-overview && protected pages
    // so user can return to their intended destination after signing in
    if (location.pathname === WEALTH_ROUTES.OVERVIEW.path) {
      return <Navigate replace to={WEALTH_ROUTES.SIGN_IN.path} />
    }
    return <Navigate replace to={`${WEALTH_ROUTES.SIGN_IN.path}?redirect=${location.pathname}`} />
  }

  return children
}

/**
 * App level components that are not tied to a specific route, such as IDLE modal
 */
const AppComponents: FC = () => {
  const { isAuthenticated } = useAuth()
  const { t } = useTranslation()

  const signOutRoute = WEALTH_ROUTES.SIGN_IN.path

  // Fetch Customer Details Immediately after Client is Authenticated
  useCustomerDetails({ enabled: isAuthenticated })

  return (
    <>
      {/* IDLE Timer for authenticated users or if in Enrollment */}
      {isAuthenticated ? (
        <IdleTimer
          title={t('idle.session_expired')}
          message={t('idle.session_timeout_description')}
          signOutRoute={signOutRoute}
        />
      ) : null}
      {/* Modal drawer container */}
      <div id={MODAL_CONTAINER_ID} />
      {/* Refresh page component */}
      <RefreshPage />
    </>
  )
}

export const Router: FC = () => {
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false) // Native Apps - Live update
  const { isLoading } = useAuth()
  const { flags } = useFlags<FeatureFlags>()

  // Native Apps - Live update
  useEffect(() => {
    const handleLiveUpdateStart = (): void => {
      setIsLoadingUpdate(true)
    }
    notifications.on(NOTIFICATIONS.LIVE_UPDATE.START, handleLiveUpdateStart)
    const handleLiveUpdateStop = (): void => {
      setIsLoadingUpdate(false)
    }
    notifications.on(NOTIFICATIONS.LIVE_UPDATE.STOP, handleLiveUpdateStop)
  }, [])

  if (flags.isUnderMaintenance) {
    return (
      <BrowserRouter>
        <MaintenancePage />
      </BrowserRouter>
    )
  }

  return (
    <BrowserRouter>
      <RouterProvider framework={RouterFramework.React}>
        <AppComponents />
        {isLoadingUpdate ? (
          <div className="fixed left-0 top-0 right-0 bottom-0 z-50 bg-white opacity-50">
            <Loader />
          </div>
        ) : null}
        {isLoading ? (
          <Layout>
            <Loader />
          </Layout>
        ) : (
          <LocationManager>
            <Routes>
              <Route path={`${WEALTH_ROUTES.SIGN_IN.path}/*`} element={<SignInPage />} />
              <Route path={`${WEALTH_ROUTES.SIGN_IN_CHALLENGE.path}/*`} element={<SignInChallengePage />} />
              <Route
                path={`${WEALTH_ROUTES.SIGN_IN_CHALLENGE_FAILURE.path}/*`}
                element={<SignInChallengeFailurePage />}
              />
              <Route
                path={`${WEALTH_ROUTES.APPLICATION_UNDER_REVIEW.path}/*`}
                element={<ApplicationUnderReviewPage />}
              />

              {/* Auth Re-entry Routes */}
              <Route
                path={AUTH_ROUTES.WELCOME_BACK.path}
                element={
                  <FeatureFlagGuard feature="rolloutReentryV2">
                    <WelcomeBackPage />
                  </FeatureFlagGuard>
                }
              />
              <Route
                path={AUTH_ROUTES.RE_ENTRY_VERIFY_EMAIL.path}
                element={
                  <FeatureFlagGuard feature="rolloutReentryV2">
                    <VerifyEmailPage />
                  </FeatureFlagGuard>
                }
              />
              <Route
                path={AUTH_ROUTES.RE_ENTRY_ENTER_PHONE.path}
                element={
                  <FeatureFlagGuard feature="rolloutReentryV2">
                    <EnterPhonePage />
                  </FeatureFlagGuard>
                }
              />
              <Route
                path={AUTH_ROUTES.RE_ENTRY_VERIFY_PHONE.path}
                element={
                  <FeatureFlagGuard feature="rolloutReentryV2">
                    <VerifyPhonePage />
                  </FeatureFlagGuard>
                }
              />
              <Route
                path={AUTH_ROUTES.RE_ENTRY_EXPIRED.path}
                element={
                  <FeatureFlagGuard feature="rolloutReentryV2">
                    <ExpiredApplicationPage />
                  </FeatureFlagGuard>
                }
              />
              {/* End of Auth Re-entry Routes */}

              <Route path={`${WEALTH_ROUTES.OVERVIEW.path}/*`} element={<OverviewPage />} />
              <Route path={WEALTH_ROUTES.CASH_ACCOUNTS_OVERVIEW.path} element={<CashAccountsOverviewPage />} />
              <Route
                path={WEALTH_ROUTES.EVERGREEN_ACCOUNTS.path}
                element={<Navigate replace to={WEALTH_ROUTES.INVESTMENTS_OVERVIEW.path} />}
              />
              <Route path={WEALTH_ROUTES.LINKED_ACCOUNTS.path} element={<LinkedAccountsPage />} />
              <Route path={WEALTH_ROUTES.EVERGREEN_INTELLIGENCE.path} element={<EvergreenIntelligencePage />} />
              <Route path={WEALTH_ROUTES.CHECKING_ACCOUNT.path} element={<CheckingAccountPage />} />
              <Route path={WEALTH_ROUTES.CHECKING_ACCOUNT_DETAILS.path} element={<CheckingAccountDetailsPage />} />
              <Route path={WEALTH_ROUTES.CHECKING_ACCOUNT_ACTIVITY.path} element={<CheckingAccountActivityPage />} />
              <Route
                path={WEALTH_ROUTES.CHECKING_ACCOUNT_ACTIVITY_DETAILS.path}
                element={<CheckingActivityDetailsPage />}
              />
              <Route path={WEALTH_ROUTES.CHECKING_ACCOUNT_DEBIT_CARD.path} element={<CheckingAccountDebitCardPage />} />
              <Route path={WEALTH_ROUTES.ACTIVATE.path} element={<AccountActivatePage />} />
              <Route
                path={WEALTH_ROUTES.CHECKING_ACCOUNT_DEBIT_CARD_ACTIVATE.path}
                element={<CheckingAccountDebitCardActivatePage />}
              />
              <Route
                path={WEALTH_ROUTES.CHECKING_ACCOUNT_DEBIT_CARD_ACTIVATE_SUCCESS.path}
                element={<DebitCardActivateSuccessPage />}
              />
              <Route path={WEALTH_ROUTES.CHECKING_ACCOUNT_DEBIT_CARD_SET_PIN.path} element={<DebitCardSetPinPage />} />
              {/* Savings */}
              <Route path={WEALTH_ROUTES.SAVINGS_ACCOUNT.path} element={<SavingsAccountPage />} />
              <Route path={WEALTH_ROUTES.SAVINGS_ACCOUNT_DETAILS.path} element={<SavingsAccountDetailsPage />} />
              <Route path={WEALTH_ROUTES.SAVINGS_ACCOUNT_ACTIVITY.path} element={<SavingsAccountActivityPage />} />
              <Route
                path={WEALTH_ROUTES.SAVINGS_ACCOUNT_ACTIVITY_DETAILS.path}
                element={<SavingsActivityDetailsPage />}
              />
              <Route path={WEALTH_ROUTES.HOME.path} element={<HomePage />} />
              {/* Investments */}
              <Route path={WEALTH_ROUTES.INVESTMENTS.path} element={<InvestmentsPage />} />
              <Route path={WEALTH_ROUTES.INVESTMENTS_OVERVIEW.path} element={<InvestmentOverviewPage />} />
              <Route path={WEALTH_ROUTES.INVESTMENTS_ACCOUNT.path} element={<InvestmentsAccountPage />} />
              <Route path={WEALTH_ROUTES.INVESTMENTS_ACCOUNT_DETAIL.path} element={<InvestmentsDetailsPage />} />
              <Route
                path={WEALTH_ROUTES.INVESTMENTS_HOLDINGS_OVERVIEW.path}
                element={<InvestmentsOverviewHoldingsPage />}
              />
              <Route
                path={WEALTH_ROUTES.INVESTMENTS_HOLDINGS_DETAILS_OVERVIEW.path}
                element={<InvestmentsOverviewHoldingsDetailsPage />}
              />
              <Route
                path={WEALTH_ROUTES.INVESTMENTS_ACCOUNT_ACTIVITY.path}
                element={<InvestmentsAccountActivityPage />}
              />
              <Route
                path={WEALTH_ROUTES.INVESTMENTS_ACCOUNT_ACTIVITY_DETAILS.path}
                element={<InvestmentsActivityDetailsPage />}
              />
              <Route path={WEALTH_ROUTES.INVESTMENTS_ACCOUNT_TRADES.path} element={<InvestmentsActivityTradesPage />} />
              <Route
                path={WEALTH_ROUTES.INVESTMENTS_ACCOUNT_TRADES_DETAILS.path}
                element={<InvestmentsActivityTradesDetailsPage />}
              />
              <Route
                path={WEALTH_ROUTES.INVESTMENTS_ACCOUNT_HOLDINGS.path}
                element={<InvestmentsAccountHoldingsPage />}
              />
              <Route
                path={WEALTH_ROUTES.INVESTMENTS_ACCOUNT_HOLDINGS_DETAILS.path}
                element={<InvestmentsAccountHoldingsDetailsPage />}
              />
              <Route
                path={WEALTH_ROUTES.INVESTMENTS_COMPANION_ACCOUNT_HOLDINGS.path}
                element={<InvestmentsCompanionAccountHoldingsPage />}
              />
              <Route
                path={WEALTH_ROUTES.INVESTMENTS_COMPANION_ACCOUNT_HOLDINGS_DETAILS.path}
                element={<InvestmentsCompanionAccountHoldingDetailsPage />}
              />
              {/* HOLDINGS ALLOCATION FOR APEX USERS (NO SECTORS JUST UNDER ASSET ALLOCATION) */}
              <Route
                path={WEALTH_ROUTES.INVESTMENTS_ALLOCATION_HOLDINGS_LIST.path}
                element={<InvestmentsAllocationHoldingsListPage />}
              />
              {/* HOLDINGS DETAILS FOR ASSET ALLOCATION HOLDINGS */}
              <Route
                path={WEALTH_ROUTES.INVESTMENTS_ALLOCATION_HOLDINGS_DETAILS.path}
                element={<InvestmentsAllocationHoldingsDetailsPage />}
              />
              {/* Personal Foundation */}
              <Route path={WEALTH_ROUTES.FOUNDATION_ACCOUNT.path} element={<PersonalFoundationPage />} />
              {/* Transfer */}
              <Route path={`${WEALTH_ROUTES.TRANSFER.path}/*`} element={<AchTransferPage />} />
              <Route path={`${WEALTH_ROUTES.TRANSFER_WIRE_REVIEW.path}/*`} element={<ReviewWirePage />} />
              <Route path={`${WEALTH_ROUTES.TRANSFER_WIRE_SUCCESS.path}/*`} element={<SuccessWirePage />} />
              <Route path={`${WEALTH_ROUTES.TRANSFER_WIRE.path}/*`} element={<WireTransferPage />} />
              <Route path={`${WEALTH_ROUTES.TRANSFER_ROLLOVER_IRA.path}/*`} element={<RolloverIRAInfoPage />} />
              <Route path={`${WEALTH_ROUTES.TRANSFER_BANK.path}/*`} element={<BankTransferPage />} />
              <Route path={`${WEALTH_ROUTES.TRANSFER_ACCOUNT.path}/*`} element={<AccountTransferPage />} />
              <Route path={WEALTH_ROUTES.TRANSFER_ACCOUNT_REVIEW.path} element={<AccountTransferReviewPage />} />
              {/* Settings */}
              <Route path={`${WEALTH_ROUTES.SETTINGS.path}/*`} element={<SettingsPage />} />
              <Route path={`${WEALTH_ROUTES.SETTINGS_PERSONAL_INFO.path}/*`} element={<SettingsPersonalInfoPage />} />
              <Route path={`${WEALTH_ROUTES.SETTINGS_PASSKEY.path}/*`} element={<SettingsPasskeyPage />} />
              <Route
                path={`${WEALTH_ROUTES.SETTINGS_PASSKEY_DETAILS.path}/*`}
                element={<SettingsPasskeyDetailsPage />}
              />
              <Route path={`${WEALTH_ROUTES.SETTINGS_BENEFICIARIES.path}/*`} element={<SettingsBeneficiariesPage />} />
              <Route
                path={`${WEALTH_ROUTES.SETTINGS_BANK_TRANSFER_ACCOUNTS.path}/*`}
                element={<SettingsExternalAccountsPage />}
              />
              <Route
                path={`${WEALTH_ROUTES.SETTINGS_BANK_TRANSFER_ACCOUNTS_DETAILS.path}/*`}
                element={<SettingsExternalAccountDetailsPage />}
              />
              {/* Documents */}
              <Route path={WEALTH_ROUTES.DOCUMENTS.path} element={<DocumentsPage />} />
              <Route path={WEALTH_ROUTES.DOCUMENTS_STATEMENTS.path} element={<StatementsPage />} />
              <Route
                path={WEALTH_ROUTES.DOCUMENTS_STATEMENTS_BY_YEAR_AND_MONTH.path}
                element={<StatementsByDatePage />}
              />
              <Route path={WEALTH_ROUTES.DOCUMENTS_TRADE_CONFIRMATION.path} element={<TradeConfirmationsPage />} />
              <Route path={WEALTH_ROUTES.DOCUMENTS_TAXES_YEAR.path} element={<TaxesByDatePage />} />
              <Route path={WEALTH_ROUTES.DOCUMENTS_LEGAL_PARTNER.path} element={<LegalPartnerPage />} />
              {/* Help Center */}
              <Route path={WEALTH_ROUTES.HELP_CENTER.path} element={<HelpCenterPage />} />
              {/* Passkey Recovery */}
              <Route path={WEALTH_ROUTES.RECOVERY.path} element={<Outlet />}>
                <Route index element={<PasskeyRecoveryPage />} />
                <Route path={WEALTH_ROUTES.RECOVERY_METHOD.nestedPath} element={<PasskeyRecoveryMethodPage />} />
                <Route path={WEALTH_ROUTES.RECOVERY_SEND_PHONE.nestedPath} element={<PasskeyRecoverySendPhonePage />} />
                <Route path={WEALTH_ROUTES.RECOVERY_SEND_EMAIL.nestedPath} element={<PasskeyRecoverySendEmailPage />} />
                <Route path={WEALTH_ROUTES.RECOVERY_MAGIC_LINK.nestedPath} element={<PasskeyRecoveryMagicLinkPage />} />
                <Route path={WEALTH_ROUTES.RECOVERY_SUBMIT.nestedPath} element={<PasskeyRecoverySubmitPage />} />
                <Route
                  path={WEALTH_ROUTES.RECOVERY_VERIFICATION.nestedPath}
                  element={<PasskeyRecoveryVerificationPage />}
                />
                <Route path={WEALTH_ROUTES.RECOVERY_PASSKEY.nestedPath} element={<PasskeyRecoveryPasskeyPage />} />
                <Route path={WEALTH_ROUTES.RECOVERY_PROBLEM.nestedPath} element={<PasskeyRecoveryProblemPage />} />
                <Route path={WEALTH_ROUTES.RECOVERY_REVIEW.nestedPath} element={<PasskeyRecoveryReviewPage />} />
              </Route>
              <Route path={WEALTH_ROUTES.ENROLLMENT.path} element={<Outlet />}>
                {Enrollment}
              </Route>
              <Route path="*" element={<NotFound404Page />} />
            </Routes>
          </LocationManager>
        )}
      </RouterProvider>
    </BrowserRouter>
  )
}
